import type { AccountSummary } from '@fruitful/types';
import * as React from 'react';
import { useState } from 'react';
import {
    BodyMediumEmphasized,
    ChevronRightIcon,
    H5Emphasized,
    Icon,
    LabelEmphasized,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { centsToFormattedDollars } from '../../../common/utils/bankingUtils';
import EditGoalAccountLinkSheet from './EditGoalAccountLinkSheet';

interface GoalBalanceButtonProps {
    linkedAccounts: Record<string, AccountSummary>;
}

const GoalBalanceButton = ({ linkedAccounts }: GoalBalanceButtonProps) => {
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    // in the future there might be more than one account linked to a goal
    const totalBalance = Object.values(linkedAccounts).reduce(
        (acc, linkedAccount) => acc + (linkedAccount.currentBalanceCents ?? 0),
        0,
    );

    // we are only allowing one account to be linked at a time for now, so just use the first linkedAccount in the array
    const goalAccountLinkId = Object.keys(linkedAccounts)[0] as string;
    const linkedAccount = Object.values(linkedAccounts)[0] as AccountSummary;
    const { institutionName, accountNumberMasked } = linkedAccount;

    return (
        <>
            <YStack
                width="100%"
                padding={12}
                borderWidth={1}
                borderColor="$gray200"
                borderRadius={12}
                flexShrink={1}
                gap={4}
                onPress={() => {
                    setIsSheetOpen(true);
                }}
                animation="button"
                pressStyle={{
                    scale: 0.95,
                }}
            >
                <BodyMediumEmphasized color="$gray1000">
                    Balance
                </BodyMediumEmphasized>
                <H5Emphasized>
                    {centsToFormattedDollars(totalBalance)}
                </H5Emphasized>
                <XStack>
                    <LabelEmphasized color="$gray1000">
                        {institutionName} *{accountNumberMasked}
                    </LabelEmphasized>
                    <View marginLeft="auto" alignSelf="center">
                        <Icon
                            icon={ChevronRightIcon}
                            size={12}
                            color="$gray900"
                        />
                    </View>
                </XStack>
            </YStack>
            {isSheetOpen && (
                <EditGoalAccountLinkSheet
                    goalAccountLinkId={goalAccountLinkId}
                    linkedAccount={linkedAccount}
                    onDismiss={() => {
                        setIsSheetOpen(false);
                    }}
                />
            )}
        </>
    );
};

export default GoalBalanceButton;
